<template>
  <div id="lav-lom-status-list">
    <v-card class="transparent" flat>
      <!-- Premium Enterprise Aqua Glass Header -->
      <div
        class="lav-dashboard-header mb-6 pa-6 rounded-xl elevation-2 position-relative overflow-hidden"
      >
        <!-- Floating Animated Background Glow & Watermark Icon -->
        <div class="header-glow-orb"></div>
        <v-icon class="header-watermark-icon"
          >mdi-file-certificate-outline</v-icon
        >

        <div
          class="d-flex flex-wrap align-center justify-space-between gap-4 position-relative z-index-1"
        >
          <div class="header-title-content">
            <div class="d-flex align-center mb-2">
              <span
                class="year-chip px-3 py-1 rounded-pill text-caption font-weight-bold mr-2 d-inline-flex align-center"
              >
                <v-icon x-small color="#FFD166" class="mr-1"
                  >mdi-calendar-star</v-icon
                >
                {{ YearName }}
              </span>
              <span
                class="system-badge text-overline font-weight-bold d-inline-flex align-center"
              >
                <v-icon x-small color="#06D6A0" class="mr-1"
                  >mdi-shield-crown-outline</v-icon
                >
                LAV MANAGEMENT
              </span>
            </div>
            <h1
              class="text-h4 font-weight-black white--text tracking-tight mb-1 d-flex align-center"
            >
              <v-icon large color="#06D6A0" class="mr-3 header-main-icon"
                >mdi-buffer</v-icon
              >
              {{ PageTitle }}
            </h1>
            <p
              class="text-subtitle-2 white--text opacity-90 mb-0 d-flex align-center"
            >
              <v-icon small color="rgba(255,255,255,0.7)" class="mr-1"
                >mdi-information-outline</v-icon
              >
              {{ PageDescription }}
            </p>
          </div>
          <div>
            <v-btn
              large
              rounded
              elevation="4"
              color="#a4c639"
              class="refresh-btn font-weight-bold text-navy px-6"
              :loading="LoadingFlag"
              @click.prevent="refreshPageData"
            >
              <v-icon left class="refresh-icon">mdi-refresh</v-icon>
              Refresh Data
            </v-btn>
          </div>
        </div>
      </div>

      <!-- ZVP Preferred Date Dialog -->
      <v-dialog v-model="zvpDatePrompt" persistent max-width="75%">
        <zvp-Preferred-date
          pageTitle="ZVP Preferred Date"
          :zvpDatePrompt="zvpDatePrompt"
          @hideDialog="hideZvpDatePrompt($event)"
          :recordData="rowData"
          v-if="zvpDatePrompt"
        ></zvp-Preferred-date>
      </v-dialog>

      <!-- Floating Speed Dial -->
      <v-container class="py-0">
        <v-speed-dial
          v-if="FloatingButtonFlag"
          v-model="fab"
          :top="top"
          :bottom="bottom"
          :right="right"
          :left="left"
          :direction="direction"
          :open-on-hover="hover"
          :transition="transition"
          :fixed="true"
        >
          <template v-slot:activator>
            <v-btn v-model="fab" color="#118AB2" dark fab elevation="6">
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else>mdi-account-circle</v-icon>
            </v-btn>
          </template>
          <v-btn
            fab
            dark
            small
            color="#073B4C"
            elevation="4"
            @click.prevent="addRecordPrompt = true"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-speed-dial>
      </v-container>

      <!-- Full-page Overlay Loader -->
      <v-overlay :value="OverlayLoadingFlag">
        <v-progress-circular
          indeterminate
          size="64"
          color="#06D6A0"
        ></v-progress-circular>
      </v-overlay>

      <v-card-text class="pa-0">
        <!-- Inline Loading Spinner -->
        <div class="d-flex justify-center my-6" v-if="LoadingFlag">
          <b-spinner
            style="width: 3rem; height: 3rem"
            variant="info"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>

        <!-- Skeleton Loader -->
        <v-container class="py-0 mb-6" v-if="StartupLoadingFlag">
          <v-skeleton-loader
            v-bind="attrs"
            type="table-tbody"
            class="rounded-xl elevation-1 pa-4"
          ></v-skeleton-loader>
        </v-container>

        <!-- Empty State Card -->
        <v-container class="py-0 mb-6" v-if="tableData1 <= 0">
          <v-card
            flat
            class="state-card pa-12 text-center rounded-xl"
            v-if="tableData2.length == 0"
          >
            <div class="state-icon-box mb-4">
              <v-icon size="48" color="#FFD166">mdi-folder-open-outline</v-icon>
            </div>
            <h3 class="text-h5 font-weight-bold white--text mb-2">
              No Records Found
            </h3>
            <p class="text-body-2 white--text opacity-70 mb-0">
              There are currently no LAV LO status records to display.
            </p>
          </v-card>
        </v-container>

        <!-- Main Content Area -->
        <v-container class="py-0 px-0" v-if="tableData1.length > 0">
          <!-- Overview Metric Cards -->
          <v-row class="mb-6">
            <!-- Metric Card 1: Deadline -->
            <v-col cols="12" sm="12" md="4">
              <div
                class="overview-card card-deadline pa-6 rounded-xl elevation-3 d-flex align-center"
              >
                <div
                  class="card-icon-box mr-4 rounded-lg d-flex align-center justify-center"
                >
                  <v-icon large color="white">mdi-calendar-clock</v-icon>
                </div>
                <div>
                  <div
                    class="text-overline font-weight-bold white--text opacity-80"
                    style="line-height: 1.2"
                  >
                    Second Half Visit Reporting Deadline
                  </div>
                  <div class="text-h5 font-weight-black white--text mt-1">
                    {{ TrainingData1.EndDate }}
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Metric Card 2: Approved Reports -->
            <v-col cols="12" sm="6" md="4">
              <div
                class="overview-card card-approved pa-6 rounded-xl elevation-3 d-flex align-center"
              >
                <div
                  class="card-icon-box mr-4 rounded-lg d-flex align-center justify-center"
                >
                  <v-icon large color="white">mdi-check-decagram</v-icon>
                </div>
                <div>
                  <div
                    class="text-overline font-weight-bold white--text opacity-80"
                    style="line-height: 1.2"
                  >
                    Reports Approved For
                  </div>
                  <div class="text-h4 font-weight-black white--text mt-1">
                    {{ TrainingData1.CompletedCount }}
                    <span class="text-subtitle-1 font-weight-medium"
                      >/ {{ TrainingData1.TotalCount }} LOs</span
                    >
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Metric Card 3: Reported Count -->
            <v-col cols="12" sm="6" md="4">
              <div
                class="overview-card card-pending pa-6 rounded-xl elevation-3 d-flex align-center"
              >
                <div
                  class="card-icon-box mr-4 rounded-lg d-flex align-center justify-center"
                >
                  <v-icon large color="#073B4C"
                    >mdi-file-document-outline</v-icon
                  >
                </div>
                <div>
                  <div
                    class="text-overline font-weight-bold text-navy opacity-80"
                    style="line-height: 1.2"
                  >
                    Second Half Visit Reported For
                  </div>
                  <div class="text-h4 font-weight-black text-navy mt-1">
                    {{ TrainingData1.PendingCount }}
                    <span class="text-subtitle-1 font-weight-medium"
                      >/ {{ TrainingData1.TotalCount }} LOs</span
                    >
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Excel Download Bar -->
          <div v-if="DownloadFlag" class="d-flex justify-end mb-6">
            <v-btn
              color="#118ab2"
              dark
              large
              rounded
              elevation="2"
              class="download-btn px-6 font-weight-bold"
              @click.prevent="
                generateExcel(
                  tableData1,
                  ExcelFields,
                  ExcelFileName,
                  'My Worksheet'
                )
              "
            >
              <v-icon left>mdi-file-excel-box</v-icon> Download Excel Report
            </v-btn>
          </div>

          <!-- Cards Data Grid -->
          <v-data-iterator
            :items="tableData1"
            :items-per-page="-1"
            hide-default-footer
          >
            <template v-slot:header> </template>

            <template v-slot:default="props">
              <v-row>
                <v-col
                  v-for="item in props.items"
                  :key="item.YearwiseLomId"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="4"
                  class="d-flex"
                >
                  <v-card
                    class="lom-status-card rounded-xl elevation-2 d-flex flex-column justify-space-between w-100"
                  >
                    <div class="lom-card-body pa-5">
                      <!-- Background Watermark Icon -->
                      <v-icon class="card-watermark-icon"
                        >mdi-office-building-outline</v-icon
                      >

                      <!-- Card Header -->
                      <div
                        class="d-flex align-center justify-space-between mb-4"
                      >
                        <div class="d-flex align-center">
                          <v-avatar
                            color="#118AB2"
                            size="40"
                            class="mr-3 white--text font-weight-bold shadow-sm"
                          >
                            <v-icon dark small>mdi-office-building</v-icon>
                          </v-avatar>
                          <div>
                            <h3
                              class="lom-name text-h6 font-weight-black white--text mb-0"
                            >
                              {{ item.LomName }}
                            </h3>
                            <span class="text-caption white--text opacity-70"
                              >LO Status Summary</span
                            >
                          </div>
                        </div>
                      </div>

                      <v-divider class="divider-glow mb-4"></v-divider>

                      <!-- Key Dates Box -->
                      <div
                        class="dates-container mb-4 pa-3 rounded-lg bg-navy-light"
                      >
                        <v-icon class="box-watermark-icon"
                          >mdi-calendar-clock-outline</v-icon
                        >
                        <div
                          class="d-flex align-center justify-space-between mb-2"
                        >
                          <span
                            class="text-caption font-weight-medium white--text opacity-80"
                          >
                            <v-icon small color="#FFD166" class="mr-1"
                              >mdi-calendar-account</v-icon
                            >
                            Presidents Date
                          </span>
                          <span
                            class="text-body-2 font-weight-bold white--text"
                          >
                            {{
                              item.LavDateTxt ? item.LavDateTxt : "Yet to wait"
                            }}
                          </span>
                        </div>

                        <div class="d-flex align-center justify-space-between">
                          <span
                            class="text-caption font-weight-medium white--text opacity-80"
                          >
                            <v-icon small color="#06D6A0" class="mr-1"
                              >mdi-calendar-check</v-icon
                            >
                            ZVP Date
                          </span>
                          <div class="d-flex align-center">
                            <span
                              class="text-body-2 font-weight-bold white--text mr-2"
                            >
                              {{ item.ZvpLavDate }}
                            </span>
                            <v-btn
                              v-if="!item.ZvpLavDateFlag"
                              x-small
                              color="#f36364"
                              class="white--text font-weight-bold px-2"
                              rounded
                              elevation="2"
                              @click.prevent="clickZvpDate(item)"
                            >
                              Selected Date
                            </v-btn>
                          </div>
                        </div>
                      </div>

                      <!-- Status Indicators Section -->
                      <div
                        class="statuses-container d-flex flex-column gap-2 mb-2"
                      >
                        <v-icon class="box-watermark-icon"
                          >mdi-shield-check-outline</v-icon
                        >
                        <!-- Status 1: Second Half Visit Status -->
                        <div
                          class="status-row d-flex align-center justify-space-between pa-2 rounded-lg"
                        >
                          <span
                            class="status-label text-caption font-weight-medium white--text opacity-90"
                          >
                            Second Half Visit Status
                          </span>
                          <v-chip
                            small
                            dark
                            :color="item.CompletedStatusColor"
                            class="status-chip font-weight-bold px-3 elevation-1"
                          >
                            {{ item.CompletedStatusTxt }}
                          </v-chip>
                        </div>

                        <!-- Status 2: Presidents Approval Status -->
                        <div
                          class="status-row d-flex align-center justify-space-between pa-2 rounded-lg"
                        >
                          <span
                            class="status-label text-caption font-weight-medium white--text opacity-90"
                          >
                            Presidents Approval Status
                          </span>
                          <v-chip
                            small
                            dark
                            :color="item.StausTxtColor"
                            class="status-chip font-weight-bold px-3 elevation-1"
                          >
                            {{ item.StausTxt }}
                          </v-chip>
                        </div>

                        <!-- Status 3: NHQ Approval Status -->
                        <div
                          class="status-row d-flex align-center justify-space-between pa-2 rounded-lg"
                        >
                          <span
                            class="status-label text-caption font-weight-medium white--text opacity-90"
                          >
                            NHQ Approval Status
                          </span>
                          <v-chip
                            small
                            dark
                            :color="item.LavNhqApprovalStatusColor"
                            class="status-chip font-weight-bold px-3 elevation-1"
                          >
                            {{ item.LavNhqApprovalStatusTxt }}
                          </v-chip>
                        </div>

                        <!-- Completed Date Row -->
                        <div
                          v-if="item.CompletedStatus == 1 && CompletedDateFlag"
                          class="status-row d-flex align-center justify-space-between pa-2 rounded-lg mt-1"
                        >
                          <span
                            class="status-label text-caption font-weight-medium white--text opacity-90"
                          >
                            <v-icon small color="#06D6A0" class="mr-1"
                              >mdi-clock-check-outline</v-icon
                            >
                            Completed Date
                          </span>
                          <span
                            class="text-body-2 font-weight-bold green--text text--lighten-2"
                          >
                            {{ item.CompletedDateTxt }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Card Actions -->
                    <div
                      class="card-action-bar pa-3 d-flex flex-wrap align-center justify-end gap-2 border-top-navy"
                    >
                      <v-tooltip bottom v-if="item.CreateFlag">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-if="item.CreateFlag"
                            @click="addData(item)"
                            target="_blank"
                            color="#118AB2"
                            small
                            class="action-btn text-white"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark left small>mdi-play-circle</v-icon>
                            Start
                          </v-btn>
                        </template>
                        <span>Send {{ item.LomName }} Second Half</span>
                      </v-tooltip>

                      <v-tooltip bottom v-if="item.EditFlag">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="editData(item)"
                            target="_blank"
                            color="#FFD166"
                            small
                            class="action-btn text-navy font-weight-bold"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark left small color="#073B4C"
                              >mdi-pencil-circle</v-icon
                            >
                            Edit
                          </v-btn>
                        </template>
                        <span>Edit {{ item.LomName }} Second Half</span>
                      </v-tooltip>

                      <v-tooltip bottom v-if="item.GeneratePdfFlag">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="regeneratePDF(item.LomTrainingId)"
                            color="#06D6A0"
                            small
                            class="action-btn text-white"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark left small>mdi-cloud-upload</v-icon>
                            Submit
                          </v-btn>
                        </template>
                        <span>Submit {{ item.LomName }} Report</span>
                      </v-tooltip>

                      <v-tooltip bottom v-if="item.DownloadFlag">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            :href="item.FilePath"
                            target="_blank"
                            color="#f36364"
                            small
                            class="action-btn text-white"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon left small>mdi-file-pdf-box</v-icon>
                            Download
                          </v-btn>
                        </template>
                        <span>Download Report</span>
                      </v-tooltip>

                      <v-tooltip bottom v-if="PreviewFlag">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="previewData(index, item)"
                            target="_blank"
                            color="#118AB2"
                            small
                            class="action-btn text-white"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark left small>mdi-file-search</v-icon>
                            Preview
                          </v-btn>
                        </template>
                        <span>{{ item.LomName }} Second Half Preview</span>
                      </v-tooltip>

                      <v-tooltip bottom v-if="showReasonButton(item)">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="openReasonPrompt(item)"
                            color="primary"
                            small
                            class="action-btn text-white"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon dark left small
                              >mdi-information-outline</v-icon
                            >
                            Reason
                          </v-btn>
                        </template>
                        <span>View Acknowledgement Reason</span>
                      </v-tooltip>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="previewRecordPrompt" max-width="75%">
      <lom-training-preview
        pageDescription=" Training Details"
        category="1"
        :showDialog="previewRecordPrompt"
        @hideDialog="hidePreviewRecordPrompt"
        :row="selectedData"
        v-if="previewRecordPrompt"
      ></lom-training-preview>
    </v-dialog>

    <v-dialog v-model="reasonRecordPrompt" max-width="75%">
      <lav-approval-status
        pageDescription="Acknowledgement Details"
        category="2"
        :zoneCode="CurrentYearId"
        :regionCode="0"
        :showLavDialog="reasonRecordPrompt"
        @hideLavDialog="reasonRecordPrompt = false"
        :rowData="selectedData"
        :viewOnly="true"
        v-if="reasonRecordPrompt"
      ></lav-approval-status>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import LomTrainingPreview from "@/view/pages/erp/ldmt/LomTrainingPreview.vue";
import ZvpPreferredDate from "@/view/pages/erp/ldmt/ZvpPreferredDate.vue";
import LavApprovalStatus from "@/view/pages/erp/ldmt/LavApprovalStatus.vue";

export default {
  mixins: [common],
  components: {
    LomTrainingPreview,
    ZvpPreferredDate,
    LavApprovalStatus,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      FloatingButtonFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,

      TrainingData1: {},
      rowData: {},
      rows: [],
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      CompletedDateFlag: false,
      OverlayLoadingFlag: false,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      reasonRecordPrompt: false,

      TableDisplayFlag1: false,

      zone: {},
      lom: {},

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      JciYearCode: "",

      search1: "",
      selected1: "",
      tableColumns1: [],
      tableData1: [],
      tableOptions1: {},

      tableColumns2: [],
      tableData2: [],
      tableOptions2: {},

      PageInfo: {},
      zvpDatePrompt: false,
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
  },
  methods: {
    showReasonButton(item) {
      if (!item) return false;
      if (item.ReasonFlag !== undefined && item.ReasonFlag !== null) {
        return !!item.ReasonFlag;
      }
      var status = item.LavApprovalStatus;
      if (status !== undefined && status !== null && status !== "") {
        return status != 1 && status != 0 && status != 2;
      }
      var txt = (
        item.LavNhqApprovalStatusTxt ||
        item.StausTxt ||
        ""
      ).toLowerCase();
      return (
        txt !== "" &&
        !txt.includes("approved") &&
        !txt.includes("completed") &&
        !txt.includes("pending") &&
        !txt.includes("yet")
      );
    },
    openReasonPrompt(item) {
      this.selectedData = item;
      this.reasonRecordPrompt = true;
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "lav",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      // this.generatePdf()
    },
    pageData() {
      console.log("pageData called");
      this.searchForm();
    },
    addData(tr) {
      console.log("addData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var url = "/lav/send-report";

        var LomCode = tr.LomId;
        console.log({ LomCode });

        var TrainingId = tr.LomTrainingId;
        console.log({ TrainingId });

        var CategoryId = tr.TrainingCategoryId;
        console.log({ CategoryId });

        var params = {
          v1: CategoryId,
          v2: LomCode,
          v3: TrainingId,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    hideZvpDatePrompt(flag) {
      console.log("hideZvpDatePrompt called");
      this.zvpDatePrompt = false;
      console.log({ flag });
      if (flag) {
        this.pageData();
      }
    },
    clickZvpDate(tr) {
      console.log("clickZvpDate called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.zvpDatePrompt = true;
        this.rowData = tr;
        console.log({ rowData });
      } else {
        var message = "Selected Date failed";
        this.toast("error", message);
      }
    },
    regeneratePDF(LomTrainingId) {
      console.log("regeneratePDF called");

      console.log("LomTrainingId=", LomTrainingId);

      var token = this.$session.get("token") || "";

      if (!LomTrainingId) {
        this.toast("error", "Training ID is missing.");
        return;
      }
      this.OverlayLoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/lom-training/regenerate-pdf";
      var upload = {
        UserInterface: 1,
        LomTraining: LomTrainingId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          if (flag == 1) {
            thisIns.toast("success", output);
            thisIns.searchForm();
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.OverlayLoadingFlag = false;
        });
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var url = "/lav/edit-report";
        var LomCode = tr.LomId;
        console.log({ LomCode });

        var TrainingId = tr.LomTrainingId;
        console.log({ TrainingId });

        var CategoryId = tr.TrainingCategoryId;
        console.log({ CategoryId });

        var params = {
          v1: CategoryId,
          v2: LomCode,
          v3: TrainingId,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    resetForm() {
      this.search = "";
      this.tableData2 = [];
      this.tableData1 = [];
      this.selected = [];
      this.$refs.form2.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewData(index, tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    searchForm() {
      console.log("searchForm called");

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? 0 : LomCode;
      console.log("LomCode=" + LomCode);

      // var validate1 = this.$refs.form2.validate();
      var validate1 = true;
      console.log("validate1=" + validate1);

      if (validate1) {
        this.TrainingData = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom-training/report";
        var upload = {
          UserInterface: 3,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.tableData1 = [];
        this.LoadingFlag = true;
        this.SearchFlag = true;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            var options = records.TableOptions;
            thisIns.tableOptions1 = options;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;
              thisIns.TrainingData1 = records.ImportantLavDates;

              thisIns.DownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;

              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.LoadingFlag = false;
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill required fields.";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
    this.tableColumns1.push(
      { text: "Paid Date", value: "PaidDateTxt" },
      { text: "Year", value: "YearName" },
      {
        text: "Subscription Description",
        value: "ContributionTypeDescription",
      },
      { text: "Paid Count", value: "PaidCount" },
      { text: "Remaining Count", value: "RemainingCount" },
      { text: "Add members", value: "actions" }
    );
  },
};
</script>
<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");
@import "~vue-tabulator/dist/scss/bootstrap/tabulator_bootstrap4";

#lav-lom-status-list,
#lav-lom-status-list *,
#lav-lom-status-list .v-btn,
#lav-lom-status-list .v-chip,
#lav-lom-status-list .v-card,
#lav-lom-status-list .text-h4,
#lav-lom-status-list .text-h5,
#lav-lom-status-list .text-h6,
#lav-lom-status-list .text-subtitle-1,
#lav-lom-status-list .text-subtitle-2,
#lav-lom-status-list .text-body-1,
#lav-lom-status-list .text-body-2,
#lav-lom-status-list .text-caption,
#lav-lom-status-list .text-overline {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif !important;
}

#lav-lom-status-list {
  background-color: #f8fafc;
  padding: 1.5rem;
  border-radius: 1rem;
  min-height: 100vh;
  animation: lavFadeIn 0.5s ease-out;

  .gap-2 {
    gap: 0.5rem;
  }
  .gap-4 {
    gap: 1rem;
  }

  /* Premium Enterprise Aqua Glass Header */
  .lav-dashboard-header {
    background: linear-gradient(
      135deg,
      rgba(7, 58, 82, 0.95) 0%,
      rgba(8, 85, 115, 0.92) 50%,
      rgba(0, 129, 167, 0.9) 100%
    ) !important;
    backdrop-filter: blur(16px) !important;
    -webkit-backdrop-filter: blur(16px) !important;
    border: 1px solid rgba(255, 255, 255, 0.22) !important;
    box-shadow: 0 12px 32px rgba(0, 129, 167, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.35) !important;
    position: relative;
    overflow: hidden;

    .header-glow-orb {
      position: absolute;
      top: -60px;
      right: 12%;
      width: 260px;
      height: 260px;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        rgba(6, 214, 160, 0.3) 0%,
        rgba(0, 180, 216, 0.15) 50%,
        transparent 75%
      );
      filter: blur(30px);
      animation: headerOrbFloat 8s ease-in-out infinite alternate;
      pointer-events: none;
    }

    .header-watermark-icon {
      position: absolute;
      right: -20px;
      bottom: -35px;
      font-size: 190px !important;
      color: rgba(255, 255, 255, 0.08) !important;
      pointer-events: none;
      user-select: none;
      transform: rotate(-10deg);
      transition: all 0.5s ease;
    }

    &:hover .header-watermark-icon {
      transform: rotate(0deg) scale(1.08);
      color: rgba(255, 255, 255, 0.15) !important;
    }

    .header-main-icon {
      animation: headerIconFloat 3s ease-in-out infinite alternate;
    }

    h1 {
      font-size: 28px !important;
      font-weight: 800 !important;
      letter-spacing: -0.5px;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    p {
      font-size: 15px !important;
      font-weight: 400;
      opacity: 0.95;
    }

    .year-chip {
      background: rgba(255, 255, 255, 0.18);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      color: #ffd166;
      border: 1px solid rgba(255, 209, 102, 0.4);
      font-size: 13px !important;
      font-weight: 700 !important;
      letter-spacing: 0.5px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    .system-badge {
      color: rgba(255, 255, 255, 0.85);
      background: rgba(255, 255, 255, 0.12);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      padding: 2px 10px;
      border-radius: 20px;
      font-size: 12px !important;
      font-weight: 800 !important;
      letter-spacing: 1.5px;
    }

    .refresh-btn {
      background-color: #a4c639 !important;
      color: #073b4c !important;
      font-size: 15px !important;
      font-weight: 700 !important;
      height: 44px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 14px rgba(164, 198, 57, 0.35) !important;

      &:hover {
        transform: translateY(-3px) scale(1.03);
        box-shadow: 0 8px 20px rgba(164, 198, 57, 0.5) !important;

        .refresh-icon {
          transform: rotate(180deg);
        }
      }

      .refresh-icon {
        transition: transform 0.5s ease;
      }
    }
  }

  /* Metric Cards */
  .overview-card {
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px -6px rgba(7, 59, 76, 0.15) !important;
    }

    &.card-deadline {
      background: linear-gradient(135deg, #118ab2 0%, #073b4c 100%);

      .card-icon-box {
        background: rgba(255, 255, 255, 0.15);
      }
    }

    &.card-approved {
      background: linear-gradient(135deg, #06d6a0 0%, #073b4c 100%);

      .card-icon-box {
        background: rgba(255, 255, 255, 0.2);
      }
    }

    &.card-pending {
      background: linear-gradient(135deg, #ffd166 0%, #f4a261 100%);
      border-color: rgba(255, 209, 102, 0.4);

      .card-icon-box {
        background: rgba(7, 59, 76, 0.12);
      }

      .text-navy {
        color: #073b4c !important;
      }
    }

    .card-icon-box {
      width: 56px;
      height: 56px;
      backdrop-filter: blur(4px);
    }

    .text-overline {
      font-size: 13px !important;
      font-weight: 700 !important;
      letter-spacing: 0.8px;
    }

    .text-h5,
    .text-h4 {
      font-size: 26px !important;
      font-weight: 800 !important;
      letter-spacing: -0.5px;
    }

    .text-subtitle-1 {
      font-size: 16px !important;
    }
  }

  /* Download Button */
  .download-btn {
    background-color: #118ab2 !important;
    font-size: 15px !important;
    font-weight: 700 !important;
    height: 44px !important;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(17, 138, 178, 0.35) !important;
    }
  }

  /* Aqua Status Summary Cards */
  .lom-status-card {
    background: linear-gradient(
      135deg,
      #073a52 0%,
      #085573 50%,
      #0081a7 100%
    ) !important;
    border: 1px solid rgba(255, 255, 255, 0.18) !important;
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 8px 25px rgba(0, 129, 167, 0.25) !important;
    position: relative;
    overflow: hidden;

    &:hover {
      transform: translateY(-6px) scale(1.01) !important;
      box-shadow: 0 20px 40px rgba(0, 129, 167, 0.4) !important;
      border-color: rgba(6, 214, 160, 0.5) !important;
    }

    .lom-card-body {
      position: relative;
      z-index: 1;
      overflow: hidden;
    }

    .card-watermark-icon {
      position: absolute;
      right: -25px;
      bottom: -25px;
      font-size: 160px !important;
      color: rgba(255, 255, 255, 0.08) !important;
      pointer-events: none;
      user-select: none;
      transform: rotate(-12deg);
      transition: all 0.4s ease;
      z-index: 0;
    }

    &:hover .card-watermark-icon {
      transform: rotate(0deg) scale(1.1);
      color: rgba(255, 255, 255, 0.14) !important;
    }

    .lom-name {
      font-size: 20px !important;
      font-weight: 800 !important;
      line-height: 1.3;
      letter-spacing: -0.3px;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .text-caption {
      font-size: 13px !important;
    }

    .divider-glow {
      border-color: rgba(255, 255, 255, 0.15) !important;
    }

    /* Glassy Finish for Key Dates Box */
    .bg-navy-light,
    .dates-container {
      background: rgba(255, 255, 255, 0.12) !important;
      backdrop-filter: blur(12px) !important;
      -webkit-backdrop-filter: blur(12px) !important;
      border: 1px solid rgba(255, 255, 255, 0.22) !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
      position: relative;
      overflow: hidden;
      z-index: 1;

      .box-watermark-icon {
        position: absolute;
        right: -10px;
        bottom: -15px;
        font-size: 90px !important;
        color: rgba(255, 255, 255, 0.07) !important;
        pointer-events: none;
        user-select: none;
        z-index: 0;
      }

      .text-caption {
        font-size: 14px !important;
        font-weight: 600 !important;
        position: relative;
        z-index: 1;
      }

      .text-body-2 {
        font-size: 15px !important;
        font-weight: 700 !important;
        position: relative;
        z-index: 1;
      }
    }

    /* Glassy Finish for Status Indicators */
    .statuses-container {
      position: relative;
      z-index: 1;
      overflow: hidden;

      .box-watermark-icon {
        position: absolute;
        right: -10px;
        bottom: -10px;
        font-size: 100px !important;
        color: rgba(255, 255, 255, 0.06) !important;
        pointer-events: none;
        user-select: none;
        z-index: 0;
      }
    }

    .status-row {
      background: rgba(255, 255, 255, 0.1) !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid rgba(255, 255, 255, 0.18) !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
      transition: all 0.3s ease !important;
      position: relative;
      z-index: 1;

      &:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        border-color: rgba(255, 255, 255, 0.35) !important;
        transform: translateX(4px);
      }

      .status-label {
        font-size: 14px !important;
        font-weight: 600 !important;
      }
    }

    .status-chip {
      font-size: 13px !important;
      font-weight: 700 !important;
      height: 28px !important;
      padding: 0 14px !important;
      letter-spacing: 0.2px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    /* Glassy Action Bar */
    .card-action-bar {
      background: rgba(0, 0, 0, 0.2) !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border-top: 1px solid rgba(255, 255, 255, 0.15) !important;
      position: relative;
      z-index: 2;

      .action-btn {
        font-size: 13px !important;
        font-weight: 700 !important;
        height: 34px !important;
        letter-spacing: 0.3px;
        transition: all 0.25s ease;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25) !important;
        }
      }
    }
  }

  /* No Records & Loading Cards */
  .state-card {
    background: #073b4c !important;
    border: 1px dashed rgba(255, 255, 255, 0.2);

    .state-icon-box {
      width: 72px;
      height: 72px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }

  /* Floating Speed Dial */
  .v-speed-dial {
    .v-btn--fab {
      transition: transform 0.3s ease;
      &:hover {
        transform: scale(1.08);
      }
    }
  }
}

@keyframes lavFadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes headerOrbFloat {
  0% {
    transform: translateY(0px) scale(1);
  }
  100% {
    transform: translateY(20px) scale(1.1);
  }
}

@keyframes headerIconFloat {
  0% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(-5px);
  }
}
</style>